import axios from 'axios';

// Backend API URL - update this based on your deployment
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// ==================== USER API ====================

export const userAPI = {
  getById: async (userId: string) => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  getByEmail: async (email: string) => {
    const response = await apiClient.get(`/users/email/${encodeURIComponent(email)}`);
    return response.data;
  },

  create: async (userData: any) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  update: async (userId: string, userData: any) => {
    const response = await apiClient.patch(`/users/${userId}`, userData);
    return response.data;
  },

  updatePremium: async (userId: string, status: any, expiresAt?: Date) => {
    const response = await apiClient.patch(`/users/${userId}/premium`, {
      status,
      expiresAt: expiresAt?.toISOString(),
    });
    return response.data;
  },
};

// ==================== TYPING TEST API ====================

export const typingTestAPI = {
  create: async (testData: any) => {
    const response = await apiClient.post('/typing-tests', testData);
    return response.data;
  },

  getUserTests: async (userId: string, limit: number = 10) => {
    const response = await apiClient.get(`/typing-tests/user/${userId}`, {
      params: { limit },
    });
    return response.data;
  },
  
  getRecentActivity: async (userId: string, limit: number = 5) => {
    const response = await apiClient.get(`/typing-tests/user/${userId}`, {
      params: { limit },
    });
    // Transform tests into activity format
    return response.data.map((test: any) => ({
      id: test._id,
      type: 'test',
      icon: 'PenTool',
      iconBg: 'bg-blue-500/20',
      description: `${test.mode} mode test`,
      timestamp: test.createdAt,
      wpm: test.wpm,
      accuracy: test.accuracy
    }));
  },
};

// ==================== LEADERBOARD API ====================

export const leaderboardAPI = {
  update: async (entryData: {
    userId: string;
    username: string;
    country: string;
    wpm: number;
    accuracy: number;

  }) => {
    const response = await apiClient.post('/leaderboard', entryData);
    return response.data;
  },

  get: async (category: string = 'wpm', limit: number = 100) => {
    const response = await apiClient.get('/leaderboard', {
      params: { category, limit },
    });
    return response.data;
  },

  getByCountry: async (country: string, category: string = 'wpm', limit: number = 100) => {
    const response = await apiClient.get(`/leaderboard/country/${country}`, {
      params: { category, limit },
    });
    return response.data;
  },
};

// ==================== PAYMENT API ====================

export const paymentAPI = {
  create: async (paymentData: any) => {
    const response = await apiClient.post('/payments', paymentData);
    return response.data;
  },

  updateStatus: async (paymentId: string, status: string, transactionHash?: string) => {
    const response = await apiClient.patch(`/payments/${paymentId}`, {
      status,
      transactionHash,
    });
    return response.data;
  },
};

// Health check
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API_URL.replace('/api', '')}/health`);
    return response.data;
  } catch (error) {
    return { status: 'error', error };
  }
};

export default apiClient;
